'use client'

import { useEffect, useState } from 'react'

import styles from '../peacecover.module.css'

const WORDS = ['artist', 'series', 'movie', 'character', 'novel'] as const
const TYPE_SPEED_MS = 95
const DELETE_SPEED_MS = 58
const HOLD_MS = 900
const SWITCH_PAUSE_MS = 220

type RotatingWordProps = {
  className?: string
  words?: readonly string[]
}

export default function RotatingWord({ className, words }: RotatingWordProps) {
  const wordList = words && words.length > 0 ? words : WORDS
  const [index, setIndex] = useState(0)
  const [text, setText] = useState(wordList[0])
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reducedMotion) {
      setText(wordList[0])
      return
    }

    const currentWord = wordList[index]
    let timer: number

    if (!isDeleting && text.length < currentWord.length) {
      timer = window.setTimeout(() => {
        setText(currentWord.slice(0, text.length + 1))
      }, TYPE_SPEED_MS)
    } else if (!isDeleting && text.length === currentWord.length) {
      timer = window.setTimeout(() => {
        setIsDeleting(true)
      }, HOLD_MS)
    } else if (isDeleting && text.length > 0) {
      timer = window.setTimeout(() => {
        setText(currentWord.slice(0, text.length - 1))
      }, DELETE_SPEED_MS)
    } else {
      timer = window.setTimeout(() => {
        setIsDeleting(false)
        setIndex((prev) => (prev + 1) % wordList.length)
      }, SWITCH_PAUSE_MS)
    }

    return () => window.clearTimeout(timer)
  }, [index, isDeleting, text, wordList])

  useEffect(() => {
    setIndex(0)
    setText(wordList[0])
    setIsDeleting(false)
  }, [wordList.join('|')])

  return (
    <em className={`${styles.rotatingWord} ${className ?? ''}`}>
      <span className={styles.rotatingWordText}>{text}</span>
      {wordList.map((word, wordIndex) => (
        <span className={styles.rotatingWordSizer} aria-hidden="true" key={`${word}-${wordIndex}`}>
          {word}
        </span>
      ))}
    </em>
  )
}
